import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm, useWatch } from "react-hook-form";
import { CreateModuleInstanceDto } from "../../../model/module/dto/module-instance/CreateModuleInstanceDto";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { getRenderedFieldByType } from "../../entries/utils/getRenderedFieldByType";
import { Flex } from "../../../components/Flex";
import { AtiraToast } from "../../../utils/AtiraToast";
import { isCustomDateInput } from "../../../utils/isCustomDateInput";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { moduleActions } from "../../../redux/module/module.slice";
import { ModuleDefinition } from "../../../model/module/types/module-definition/ModuleDefinition";
import isEmpty from "lodash/isEmpty";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { Drawer } from "../../../components/Drawer";
import { PageMeta } from "../../../model/shared/meta/PageMeta";
import { DrawerDefaultsExtra } from "../../tasks/components/DrawerDefaultsExtra";
import { Input } from "../../../components/Input";
import { InputTypes } from "../../../model/form/types/InputTypes.enum";
import { parseDateKey } from "../../../utils/Date";

type FixedModuleInstanceCreateDrawerProps = {
  open: boolean;
  onClose: VoidFunction;
  moduleDefinition?: ModuleDefinition | null;
};

export const FixedModuleInstanceCreateDrawer: React.FC<
  FixedModuleInstanceCreateDrawerProps
> = ({ open = false, onClose, moduleDefinition }) => {
  const [loading, setLoading] = useState(false);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { control, setError, reset, getValues, handleSubmit } =
    useForm<CreateModuleInstanceDto>();

  const watchedValues = useWatch({
    control,
    name: ["fields", "name"],
    defaultValue: { fields: {}, name: "" },
  });
  const [fields, name] = watchedValues;

  const requiredInputs = useMemo(() => {
    return moduleDefinition?.inputs?.filter(
      (input) => input?.settings?.required === true,
    );
  }, [moduleDefinition?.inputs]);

  const renderedInputs = useMemo(() => {
    return moduleDefinition?.inputs?.map((input) => (
      <Controller
        key={input._id}
        name={`fields.${input.name}`}
        control={control}
        rules={{
          required: !!input?.settings?.required,
          maxLength: {
            value: input?.settings?.maxChar || Lengths.DESCRIPTION,
            message: t("error.length.less", {
              length: input?.settings?.maxChar || Lengths.DESCRIPTION,
            }),
          },
        }}
        render={({ field, fieldState }) =>
          getRenderedFieldByType(
            input,
            field,
            fieldState,
            input.label,
            undefined,
            { possibleBookedSlots: moduleDefinition?._possibleBookedSlots },
          )
        }
      />
    ));
  }, [
    moduleDefinition?.inputs,
    moduleDefinition?._possibleBookedSlots,
    control,
    t,
  ]);

  const isDisabled = useMemo(() => {
    if (!name) {
      return true;
    }

    return requiredInputs?.some((input) => {
      if (input.type.toLowerCase() === InputTypes.DATE.toLowerCase()) {
        return !fields?.[input.name];
      }

      return isEmpty(fields?.[input.name]);
    });
  }, [fields, name, requiredInputs]);

  const localOnClose = () => {
    onClose();
    reset({
      name: "",
      fields: {},
      moduleDefinitionId: moduleDefinition?._id || "",
      userId,
    });
  };

  const onSave = async () => {
    try {
      setLoading(true);

      const dto = getValues();

      const normalizedFields = parseDateKey(dto.fields || {}, (name) => {
        const parentInput = moduleDefinition?.inputs?.find(
          (i) => i.name === name,
        );

        if (!parentInput) {
          return false;
        }

        return isCustomDateInput(parentInput.type, parentInput.dateInputConfig);
      });

      const payload = {
        name: dto.name,
        fields: normalizedFields,
        moduleDefinitionId: moduleDefinition?._id,
        userId,
      } as CreateModuleInstanceDto;

      await dispatch(moduleActions.createModuleInstance(payload)).unwrap();

      await dispatch(
        moduleActions.getModuleInstances({
          userId,
          moduleDefinitionId: moduleDefinition?._id!,
          keyword: "",
          meta: PageMeta.create({
            page: 0,
            count: 1000,
          }),
        }),
      ).unwrap();

      localOnClose();

      AtiraToast.success(t("modules.instance.create.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  if (isEmpty(moduleDefinition)) {
    return null;
  }

  return (
    <Drawer
      open={open}
      onClose={localOnClose}
      title={t("modules.instance.create.title", {
        moduleDefinition: moduleDefinition.name,
      })}
      maskClosable={false}
      extra={
        <DrawerDefaultsExtra
          onSubmit={handleSubmit(onSave)}
          onCancel={localOnClose}
          config={{
            loading,
            disabled: isDisabled,
          }}
        />
      }
      size="large"
      key={moduleDefinition._id}
    >
      <Flex width={"100%"} flexDirection="column" gap="m">
        <Controller
          control={control}
          name="name"
          rules={{
            required: {
              value: true,
              message: t("common.fieldRequired"),
            },
            maxLength: {
              value: Lengths.DESCRIPTION,
              message: t("error.length.less", {
                name: t("modules.instance.name"),
                length: Lengths.DESCRIPTION,
              }),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              title={t("modules.instance.name.title")}
              value={value}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              maxLength={Lengths.DESCRIPTION}
              info={t("modules.instance.name.info")}
              required
            />
          )}
        />

        {renderedInputs}
      </Flex>
    </Drawer>
  );
};
